import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewerModel } from '@models/Viewer/ViewerModel';
import { TariffPlanCodeEnum } from '@constants';
import type { SubscriptionPlanItemStyleEnum } from '@components';

export interface ISelectedSubscription {
  style: SubscriptionPlanItemStyleEnum;
  code: TariffPlanCodeEnum;
}

export interface IOptions {
  withInvite?: boolean;
}

export function useSubscriptionPlansLayout(
  selectedSubscriptions: ISelectedSubscription[],
  options?: IOptions,
) {
  const viewer = useViewerModel();
  const { t, i18n } = useTranslation();

  const subscriptions = useMemo(
    () =>
      selectedSubscriptions.map((s) => {
        const tKeyForPlan = `common.subscriptionPlans.${s.code}`;
        const tariffPlan = viewer.tariffPlans.byCode[s.code];

        const currentPrice = tariffPlan?.price.formattedCurrent ?? '';
        const regularPrice = tariffPlan?.price?.hasDiscount
          ? tariffPlan?.price?.formattedRegular
          : '';
        const subtitle = tariffPlan?.autoupgradePlan
          ? t(`${tKeyForPlan}.subtitleAutoupgrade`, {
              price:
                tariffPlan?.autoupgradePlan?.price.formattedRegular,
            })
          : '';

        const yearlySave = tariffPlan?.save(
          TariffPlanCodeEnum.Monthly,
        );

        const promoText =
          options?.withInvite && i18n.exists(`${tKeyForPlan}.promo`)
            ? t(`${tKeyForPlan}.promo`, { price: currentPrice })
            : null;

        const badgeText = yearlySave
          ? t(`${tKeyForPlan}.badge`, { price: yearlySave })
          : null;

        return {
          badge: badgeText,
          promo: promoText,
          title: t(`${tKeyForPlan}.title`),
          time: t(`${tKeyForPlan}.time`),
          features: tariffPlan?.benefits,
          regularPrice: regularPrice,
          currentPrice: currentPrice,
          style: s.style,
          code: s.code,
          subtitle,
        };
      }),
    [i18n.language, viewer.tariffPlans.byCode],
  );

  return subscriptions;
}
